// ============================================================================
// The folder tree of the Files app, read from the File records on the device
// (docs/areas/files.md). Pure and tested (tree.test.ts): children, the way up
// (breadcrumbs), what a folder holds (for a delete as one group), sorting,
// search by name, Recent and Starred.
// ============================================================================

import type { FileResource } from '@/kinds/file/apply'

export type SortKey = 'name' | 'modified' | 'size' | 'kind'
export interface Sort {
  key: SortKey
  desc?: boolean
}

/** Live things directly in a folder (`null` is the top). */
export const childrenOf = (all: readonly FileResource[], folder: string | null): FileResource[] =>
  all.filter((f) => !f.deleted_at && (f.spec.parent ?? null) === folder)

/** The folders from the top down to `id` (itself included when it is a folder). Stops on a loop or a gap. */
export const pathTo = (all: readonly FileResource[], id: string | null): FileResource[] => {
  const byId = new Map(all.map((f) => [f.id, f]))
  const out: FileResource[] = []
  const seen = new Set<string>()
  let cur = id ? byId.get(id) : undefined
  while (cur && !seen.has(cur.id) && out.length < 64) {
    seen.add(cur.id)
    if (cur.spec.folder) out.unshift(cur)
    cur = cur.spec.parent ? byId.get(cur.spec.parent) : undefined
  }
  return out
}

/** Everything under a folder, deepest first (the order a delete takes them). Live ones only. */
export const descendants = (all: readonly FileResource[], id: string): FileResource[] => {
  const kids = new Map<string, FileResource[]>()
  for (const f of all) {
    if (f.deleted_at || !f.spec.parent) continue
    const list = kids.get(f.spec.parent) ?? []
    list.push(f)
    kids.set(f.spec.parent, list)
  }
  const out: FileResource[] = []
  const seen = new Set<string>([id])
  const walk = (at: string) => {
    for (const k of kids.get(at) ?? []) {
      if (seen.has(k.id)) continue
      seen.add(k.id)
      walk(k.id)
      out.push(k)
    }
  }
  walk(id)
  return out
}

/** Whether `id` is `folder` or somewhere under it (a move into it would be a loop). */
export const isWithin = (all: readonly FileResource[], id: string, folder: string): boolean =>
  id === folder || pathTo(all, folder).some((f) => f.id === id)

const modified = (f: FileResource) => f.status.uploaded_at ?? f.updated_at

/** Folders first, then by the key; names compare like a person would (numbers by value). */
export const sortFiles = (list: readonly FileResource[], sort: Sort): FileResource[] => {
  const coll = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
  const dir = sort.desc ? -1 : 1
  const ext = (f: FileResource) => (f.spec.name.includes('.') ? f.spec.name.split('.').pop()! : '')
  return [...list].sort((a, b) => {
    if (!!a.spec.folder !== !!b.spec.folder) return a.spec.folder ? -1 : 1
    let c = 0
    if (sort.key === 'modified') c = modified(a) < modified(b) ? -1 : modified(a) > modified(b) ? 1 : 0
    else if (sort.key === 'size') c = (a.spec.size ?? 0) - (b.spec.size ?? 0)
    else if (sort.key === 'kind') c = coll.compare(ext(a), ext(b))
    return dir * c || coll.compare(a.spec.name, b.spec.name)
  })
}

/** Live things whose name has every word of the query, best (name starts with it) first, capped. */
export const searchFiles = (all: readonly FileResource[], q: string, cap = 100): FileResource[] => {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return []
  const first = words[0]!
  return all
    .filter((f) => !f.deleted_at && words.every((w) => f.spec.name.toLowerCase().includes(w)))
    .sort((a, b) => {
      const sa = a.spec.name.toLowerCase().startsWith(first) ? 0 : 1
      const sb = b.spec.name.toLowerCase().startsWith(first) ? 0 : 1
      return sa - sb || (modified(a) < modified(b) ? 1 : -1)
    })
    .slice(0, cap)
}

/** The files touched most recently (no folders), capped: Show less. */
export const recentFiles = (all: readonly FileResource[], cap = 50): FileResource[] =>
  all
    .filter((f) => !f.deleted_at && !f.spec.folder)
    .sort((a, b) => (modified(a) < modified(b) ? 1 : -1))
    .slice(0, cap)

export const starredFiles = (all: readonly FileResource[]): FileResource[] =>
  sortFiles(
    all.filter((f) => !f.deleted_at && f.spec.starred),
    { key: 'name' },
  )

/** Deleted files and folders, newest first, without those inside a deleted folder (restoring the folder brings them). */
export const trashOf = (all: readonly FileResource[], cap = 200): FileResource[] => {
  const byId = new Map(all.map((f) => [f.id, f]))
  return all
    .filter((f) => {
      if (!f.deleted_at || f.status.purged_at) return false
      const parent = f.spec.parent ? byId.get(f.spec.parent) : undefined
      return !parent?.deleted_at
    })
    .sort((a, b) => (a.deleted_at! < b.deleted_at! ? 1 : -1))
    .slice(0, cap)
}

/** How close a child's delete must be to its folder's to count as the same delete. */
const SAME_DELETE_MS = 60_000

/** What went into the Trash with a folder (deleted with it, one group), for its Restore. */
export const deletedWith = (all: readonly FileResource[], folder: FileResource): FileResource[] => {
  if (!folder.deleted_at) return []
  const at = Date.parse(folder.deleted_at)
  const kids = new Map<string, FileResource[]>()
  for (const f of all) {
    if (!f.spec.parent) continue
    const list = kids.get(f.spec.parent) ?? []
    list.push(f)
    kids.set(f.spec.parent, list)
  }
  const out: FileResource[] = []
  const walk = (id: string) => {
    for (const k of kids.get(id) ?? []) {
      if (!k.deleted_at || Math.abs(Date.parse(k.deleted_at) - at) > SAME_DELETE_MS) continue
      out.push(k)
      walk(k.id)
    }
  }
  walk(folder.id)
  return out
}

/** A name not yet taken in a folder: "Photos", "Photos 2", … ("a.txt" → "a 2.txt"). */
export const freeName = (siblings: readonly FileResource[], want: string): string => {
  const taken = new Set(siblings.map((s) => s.spec.name.toLowerCase()))
  if (!taken.has(want.toLowerCase())) return want
  const dot = want.lastIndexOf('.')
  const stem = dot > 0 ? want.slice(0, dot) : want
  const ext = dot > 0 ? want.slice(dot) : ''
  for (let n = 2; ; n++) {
    const cand = `${stem} ${n}${ext}`
    if (!taken.has(cand.toLowerCase())) return cand
  }
}
